import { QUERY_KEYS } from '@avanta/shared/constants/common';
import { fetchCenterDetailsQueryOptions } from '@avanta/shared/hooks/useFetchCenterDetails';
import { fetchPsychologistDetailsQueryOptions } from '@avanta/shared/hooks/useFetchPsychologistDetails';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { AppointmentService } from 'services/appointments';
import { useEnterpriseId } from 'stores/user';
import {
  type GetEnterpriseAppointmentsParams,
  type AppointmentWithDetails,
} from 'types/appointments';

export const useFetchEnterpriseAppointments = (
  params?: GetEnterpriseAppointmentsParams,
  enabled = true
): UseQueryResult<AppointmentWithDetails[]> => {
  const enterpriseId = useEnterpriseId();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [QUERY_KEYS.FETCH_ENTERPRISE_APPOINTMENTS, enterpriseId, params],
    queryFn: async () => {
      const { data: appointments } = await AppointmentService.getEnterpriseAppointments(
        enterpriseId,
        params
      );

      const appointmentResults = await Promise.allSettled(
        appointments.map(async appointment => {
          const [psychologist, center] = await Promise.all([
            queryClient.fetchQuery(
              fetchPsychologistDetailsQueryOptions(appointment.psychologistId)
            ),
            queryClient.fetchQuery(fetchCenterDetailsQueryOptions(appointment.backofficeCenterId)),
          ]);

          return {
            ...appointment,
            psychologist: {
              name: psychologist.name,
              surname: psychologist.surname,
            },
            center: {
              name: center.name,
              city: center.city,
            },
          };
        })
      );

      return appointmentResults.flatMap(result =>
        result.status === 'fulfilled' ? [result.value] : []
      );
    },
    placeholderData: previousData => previousData,
    enabled: Boolean(enterpriseId) && enabled,
  });
};
